import { Addresses } from '@components/Address/Addresses';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiLink, NoddiSwitch } from 'noddi-ui-web';
import { customerRoutes, formatPhoneNumber } from 'noddi-util';
import { getLanguagePropertiesFromCountryCode, useAuthContext } from 'noddi-web';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { useDataQualityPromptActions, useIsDataQualityPromptDisabled } from '../../../stores/dataQualityPromptStore';

export const Account = () => {
  const { userData } = useAuthContext();
  const userId = userData?.user.id;
  const isPromptDisabled = useIsDataQualityPromptDisabled(userId);
  const { setUserPromptDisabled } = useDataQualityPromptActions();
  const { currentBrand } = useCurrentBrand();

  // Served from the cache the app root fills. The switch only means something where the brand shows the notices.
  const { data: brand } = noddiAsync.useGet({
    type: URLKeys.getByDomainBrand,
    input: { brandDomain: currentBrand }
  });

  const {
    data: user,
    error: userError,
    isPending: isUserPending
  } = noddiAsync.useGet({
    type: URLKeys.getAccount,
    input: { userId: userData?.user.id as number },
    queryConfig: {
      enabled: !!userData?.user.id
    }
  });

  if (!userData || isUserPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (userError) {
    return <ApiErrorMessageWithTrans error={[userError]} />;
  }

  return (
    <ContentWrapperCustomerApp className='flex flex-col gap-5' title={t`My account`}>
      <NoddiBasicCard className='flex flex-col gap-2'>
        <p className='mb-2 text-xl font-semibold'>
          {user.firstName} {user.lastName}
        </p>
        {user.phoneNumber && (
          <div className='flex items-center gap-2'>
            <NoddiIcon name='Phone' size='medium' />
            <p>{formatPhoneNumber(user.phoneNumber)}</p>
          </div>
        )}
        <div className='flex items-center gap-2'>
          <NoddiIcon name='Home' size='medium' />
          <p>{user.email}</p>
        </div>

        {user.languageCode && (
          <p className='flex items-center gap-2'>
            <span className='text-xl'>{getLanguagePropertiesFromCountryCode(user.languageCode).flag}</span>
            <span>{getLanguagePropertiesFromCountryCode(user.languageCode).name}</span>
          </p>
        )}

        <div className='flex w-full flex-col items-end gap-2'>
          <NoddiLink startIcon='Edit' size='small' to={customerRoutes.editProfile.getPath()} variant='primary'>
            {t`Edit account information`}
          </NoddiLink>
        </div>
      </NoddiBasicCard>

      <Addresses />

      {userId != null && brand?.dataQualityPrompt === true && (
        <NoddiBasicCard className='flex flex-col gap-3'>
          <div className='flex items-center justify-between gap-4'>
            <p className='text-lg font-semibold'>{t`Booking detail notices`}</p>
            <NoddiSwitch
              checked={!isPromptDisabled}
              onCheckedChange={(checked) => setUserPromptDisabled(userId, !checked)}
              aria-label={t`Booking detail notices`}
            />
          </div>
          <p className='text-sm text-muted-foreground'>
            {t`These notices only appear when there are confirmed issues with your details that prevent bookings, such as a missing or invalid email address, or an incomplete invoice address. They are enabled by default so we can tell you what to fix before it blocks a booking. You can turn them off here at any time.`}
          </p>
        </NoddiBasicCard>
      )}
    </ContentWrapperCustomerApp>
  );
};
